import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useJsApiLoader } from '@react-google-maps/api';
import { reportWarning } from '../utils/logger';

interface GoogleMapsContextType {
    isLoaded: boolean;
}

const GoogleMapsContext = createContext<GoogleMapsContextType>({ isLoaded: false });

const LIBRARIES: ("places")[] = ['places'];

/**
 * Aquí es donde se dispara la descarga de verdad.
 *
 * Está en un componente aparte por una razón concreta: `useJsApiLoader` es un
 * hook y los hooks no se pueden llamar dentro de un `if`. Sacándolo a su propio
 * componente, la decisión de cargar o no cargar pasa a ser «monto esto o no lo
 * monto», que sí se puede condicionar.
 *
 * No pinta nada; solo carga.
 */
const CargadorMaps: React.FC<{ apiKey: string; alCambiar: (v: boolean) => void }> = ({
    apiKey,
    alCambiar,
}) => {
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: apiKey,
        libraries: LIBRARIES,
    });

    useEffect(() => {
        alCambiar(isLoaded);
    }, [isLoaded, alCambiar]);

    // The js-api-loader's per-retry console.error is suppressed (consolePatch),
    // so this terminal signal is the ONE report that keeps a real Maps-CDN/key
    // outage visible in the dashboard. Side-effect only — isLoaded semantics
    // and render output are untouched.
    useEffect(() => {
        if (loadError) {
            reportWarning(
                '[GoogleMapsProvider] Maps script failed to load after retries: ' + loadError.message,
                { component: 'GoogleMapsProvider' },
            );
        }
    }, [loadError]);

    return null;
};

/**
 * Google Maps, pero no antes de que haga falta.
 *
 * Este proveedor envuelve la aplicación entera, y hasta ahora eso significaba
 * que la API de Google Maps se descargaba SIEMPRE — también en la landing, que
 * es una página de marketing sin un solo mapa. Medido en el build de
 * producción, esa carga eran 2.895 KB en ocho archivos (`places.js`, `main.js`,
 * `util.js` y dos bloques de 1.292 KB), más que todo el resto de la página
 * junta. En un teléfono con datos, la persona pagaba la API de mapas antes de
 * leer la primera frase.
 *
 * La regla es una sola y deja el resto de la aplicación exactamente como
 * estaba: en `/` (la landing) no se carga; en cualquier otra ruta sí. Y una vez
 * armado se queda armado, así que pasar de la landing al panel carga Maps y ya
 * no vuelve a decidirse nada.
 *
 * Si algún día la landing necesita mapas —un buscador de contratistas por
 * zona, por ejemplo—, basta con quitar la excepción de `enLanding`.
 */
export const GoogleMapsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '').replace(/['"]/g, '').trim();
    const { pathname } = useLocation();
    const enLanding = pathname === '/';

    const [isLoaded, setIsLoaded] = useState(false);
    // Arranca armado si la primera pantalla ya no es la landing, para no perder
    // un cuadro cuando alguien entra directo a /dashboard.
    const [armado, setArmado] = useState(!enLanding);

    useEffect(() => {
        if (!enLanding) setArmado(true);
    }, [enLanding]);

    if (!apiKey) {
        console.debug('[GoogleMapsProvider] Google Maps API key is not set in .env. Maps features will fail gracefully.');
    }

    const valor = useMemo(() => ({ isLoaded }), [isLoaded]);

    return (
        <GoogleMapsContext.Provider value={valor}>
            {armado && <CargadorMaps apiKey={apiKey} alCambiar={setIsLoaded} />}
            {children}
        </GoogleMapsContext.Provider>
    );
};

export const useGoogleMaps = () => useContext(GoogleMapsContext);
